export interface SafeArea {
	/** Position as percentage of image dimensions (0-100) */
	xPercent: number;
	yPercent: number;
	widthPercent: number;
	heightPercent: number;
	/** Area as percentage of total image (0-100) */
	areaPercent: number;
	/** Which corner this area is connected to */
	corner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export interface EdgeSafeAreas {
	top: SafeArea | null;
	right: SafeArea | null;
	bottom: SafeArea | null;
	left: SafeArea | null;
	topLeft: SafeArea | null;
	topRight: SafeArea | null;
	bottomLeft: SafeArea | null;
	bottomRight: SafeArea | null;
}

interface AreaOfInterest {
	x: number;
	y: number;
	width: number;
	height: number;
	origin: string;
}

/**
 * Analyze an image to find safe areas for text placement
 * Uses area of interest (AOI) data from data attributes if available
 * No image loading required - works purely from AOI percentages
 */
export function analyzeImageSafeAreas(imgEl: HTMLImageElement): EdgeSafeAreas {
	// Read AOI percentages from data attributes
	const aoiPercent = readAOIPercentsFromElement(imgEl);

	if (!aoiPercent) {
		// No AOI data, return default corner areas
		return createDefaultEdgeAreas();
	}

	// Calculate safe areas from edges up to AOI boundaries (in percentages)
	return calculateEdgeAreasAroundAOI(aoiPercent);
}

/**
 * Read area of interest percentages from img element data attributes
 */
function readAOIPercentsFromElement(imgEl: HTMLImageElement): AreaOfInterest | null {
	const xPercent = imgEl.dataset.aoiXPercent;
	const yPercent = imgEl.dataset.aoiYPercent;
	const widthPercent = imgEl.dataset.aoiWidthPercent;
	const heightPercent = imgEl.dataset.aoiHeightPercent;
	const origin = imgEl.dataset.aoiOrigin;

	if (!xPercent || !yPercent || !widthPercent || !heightPercent) {
		return null;
	}

	// Store as percentages (0-100)
	return {
		x: Number.parseFloat(xPercent),
		y: Number.parseFloat(yPercent),
		width: Number.parseFloat(widthPercent),
		height: Number.parseFloat(heightPercent),
		origin: origin || 'unknown'
	};
}

/**
 * Create safe areas from each edge and corner up to the AOI boundaries
 * All calculations done in percentages (0-100)
 */
function calculateEdgeAreasAroundAOI(aoiPercent: AreaOfInterest): EdgeSafeAreas {
	// Top: from top edge (0%) down to AOI top
	const top = aoiPercent.y > 0 ? createSafeAreaFromPercent(0, 0, 100, aoiPercent.y, 'top-left') : null;

	// Right: from AOI right edge to right edge (100%)
	const right =
		aoiPercent.x + aoiPercent.width < 100
			? createSafeAreaFromPercent(
					aoiPercent.x + aoiPercent.width,
					0,
					100 - (aoiPercent.x + aoiPercent.width),
					100,
					'top-right'
				)
			: null;

	// Bottom: from AOI bottom edge to bottom edge (100%)
	const bottom =
		aoiPercent.y + aoiPercent.height < 100
			? createSafeAreaFromPercent(
					0,
					aoiPercent.y + aoiPercent.height,
					100,
					100 - (aoiPercent.y + aoiPercent.height),
					'bottom-left'
				)
			: null;

	// Left: from left edge (0%) to AOI left edge
	const left = aoiPercent.x > 0 ? createSafeAreaFromPercent(0, 0, aoiPercent.x, 100, 'top-left') : null;

	// Corner areas - combine adjacent edges
	const topLeft = calculateCornerArea(aoiPercent, 'top-left');
	const topRight = calculateCornerArea(aoiPercent, 'top-right');
	const bottomLeft = calculateCornerArea(aoiPercent, 'bottom-left');
	const bottomRight = calculateCornerArea(aoiPercent, 'bottom-right');

	return {
		top,
		right,
		bottom,
		left,
		topLeft,
		topRight,
		bottomLeft,
		bottomRight
	};
}

/**
 * Calculate corner safe area by finding the space in the specified corner
 */
function calculateCornerArea(aoiPercent: AreaOfInterest, corner: SafeArea['corner']): SafeArea | null {
	const aoiRight = aoiPercent.x + aoiPercent.width;
	const aoiBottom = aoiPercent.y + aoiPercent.height;

	switch (corner) {
		case 'top-left':
			if (aoiPercent.x > 0 && aoiPercent.y > 0) {
				return createSafeAreaFromPercent(0, 0, aoiPercent.x, aoiPercent.y, 'top-left');
			}
			break;
		case 'top-right':
			if (aoiRight < 100 && aoiPercent.y > 0) {
				return createSafeAreaFromPercent(aoiRight, 0, 100 - aoiRight, aoiPercent.y, 'top-right');
			}
			break;
		case 'bottom-left':
			if (aoiPercent.x > 0 && aoiBottom < 100) {
				return createSafeAreaFromPercent(0, aoiBottom, aoiPercent.x, 100 - aoiBottom, 'bottom-left');
			}
			break;
		case 'bottom-right':
			if (aoiRight < 100 && aoiBottom < 100) {
				return createSafeAreaFromPercent(aoiRight, aoiBottom, 100 - aoiRight, 100 - aoiBottom, 'bottom-right');
			}
			break;
	}

	return null;
}

/**
 * Create default corner areas when no AOI is available
 * Returns 40% from each corner
 */
function createDefaultEdgeAreas(): EdgeSafeAreas {
	const cornerSize = 40; // 40% of image dimensions

	return {
		top: null,
		right: null,
		bottom: null,
		left: null,
		topLeft: createSafeAreaFromPercent(0, 0, cornerSize, cornerSize, 'top-left'),
		topRight: createSafeAreaFromPercent(100 - cornerSize, 0, cornerSize, cornerSize, 'top-right'),
		bottomLeft: createSafeAreaFromPercent(0, 100 - cornerSize, cornerSize, cornerSize, 'bottom-left'),
		bottomRight: createSafeAreaFromPercent(
			100 - cornerSize,
			100 - cornerSize,
			cornerSize,
			cornerSize,
			'bottom-right'
		)
	};
}

/**
 * Create a SafeArea object from percentage values
 * All values in percentages (0-100)
 * Returns null if area is less than 5% along either axis
 */
function createSafeAreaFromPercent(
	xPercent: number,
	yPercent: number,
	widthPercent: number,
	heightPercent: number,
	corner: SafeArea['corner']
): SafeArea | null {
	// Round all values to integers
	const roundedX = Math.round(Math.max(0, xPercent));
	const roundedY = Math.round(Math.max(0, yPercent));
	const roundedWidth = Math.round(Math.max(0, Math.min(widthPercent, 100 - xPercent)));
	const roundedHeight = Math.round(Math.max(0, Math.min(heightPercent, 100 - yPercent)));

	// Reject areas less than 5% along either axis
	if (roundedWidth < 5 || roundedHeight < 5) {
		return null;
	}

	const areaPercent = Math.round((roundedWidth * roundedHeight) / 100);

	return {
		xPercent: roundedX,
		yPercent: roundedY,
		widthPercent: roundedWidth,
		heightPercent: roundedHeight,
		areaPercent,
		corner
	};
}

/**
 * Helper function to visualize safe areas on an image (for debugging)
 * Creates a transparent overlay canvas with just the AOI and safe area markers
 */
export function visualizeSafeAreas(imgEl: HTMLImageElement, areas: EdgeSafeAreas): HTMLCanvasElement {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return canvas;
	}

	// Set canvas to match rendered image size (not natural size)
	canvas.width = imgEl.clientWidth || 800;
	canvas.height = imgEl.clientHeight || 600;

	// Draw AOI (Area of Interest) if present
	const aoiXPercent = imgEl.dataset.aoiXPercent;
	const aoiYPercent = imgEl.dataset.aoiYPercent;
	const aoiWidthPercent = imgEl.dataset.aoiWidthPercent;
	const aoiHeightPercent = imgEl.dataset.aoiHeightPercent;

	if (aoiXPercent && aoiYPercent && aoiWidthPercent && aoiHeightPercent) {
		// Convert percentages to pixels based on canvas dimensions
		const x = (Number.parseFloat(aoiXPercent) / 100) * canvas.width;
		const y = (Number.parseFloat(aoiYPercent) / 100) * canvas.height;
		const width = (Number.parseFloat(aoiWidthPercent) / 100) * canvas.width;
		const height = (Number.parseFloat(aoiHeightPercent) / 100) * canvas.height;

		// Draw AOI with red outline and semi-transparent red fill
		ctx.fillStyle = 'rgba(255, 0, 0, 0.2)';
		ctx.fillRect(x, y, width, height);
		ctx.strokeStyle = 'rgba(255, 0, 0, 1)';
		ctx.lineWidth = 4;
		ctx.strokeRect(x, y, width, height);

		// Draw label
		ctx.fillStyle = 'red';
		ctx.font = 'bold 20px sans-serif';
		ctx.fillText('AOI', x + 10, y + 30);
	}

	// Draw safe areas with different colors
	const colors: Record<string, string> = {
		'top-left': 'rgba(0, 255, 0, 0.3)',
		'top-right': 'rgba(0, 0, 255, 0.3)',
		'bottom-left': 'rgba(255, 255, 0, 0.3)',
		'bottom-right': 'rgba(255, 0, 255, 0.3)',
		top: 'rgba(0, 255, 128, 0.3)',
		right: 'rgba(128, 0, 255, 0.3)',
		bottom: 'rgba(255, 128, 0, 0.3)',
		left: 'rgba(128, 255, 0, 0.3)'
	};

	const drawArea = (area: SafeArea | null) => {
		if (!area || !ctx) return;

		// Convert percentages to pixels
		const x = (area.xPercent / 100) * canvas.width;
		const y = (area.yPercent / 100) * canvas.height;
		const width = (area.widthPercent / 100) * canvas.width;
		const height = (area.heightPercent / 100) * canvas.height;

		ctx.fillStyle = colors[area.corner] || 'rgba(128, 128, 128, 0.3)';
		ctx.fillRect(x, y, width, height);

		// Draw border
		ctx.strokeStyle = (colors[area.corner] || 'rgba(128, 128, 128, 0.3)').replace('0.3', '1');
		ctx.lineWidth = 2;
		ctx.strokeRect(x, y, width, height);

		// Draw label
		const label = `${area.corner}: ${area.areaPercent.toFixed(1)}%`;
		ctx.fillStyle = 'white';
		ctx.font = '16px sans-serif';
		ctx.strokeStyle = 'black';
		ctx.lineWidth = 3;
		ctx.strokeText(label, x + 5, y + 20);
		ctx.fillText(label, x + 5, y + 20);
	};

	drawArea(areas.topLeft);
	drawArea(areas.topRight);
	drawArea(areas.bottomLeft);
	drawArea(areas.bottomRight);
	drawArea(areas.top);
	drawArea(areas.right);
	drawArea(areas.bottom);
	drawArea(areas.left);

	return canvas;
}
